import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  ReceiptLong as ReceiptLongIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  WarningAmber as WarningAmberIcon,
  FlashOn as FlashOnIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Description as DescriptionIcon,
  Hotel as HotelIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import {
  getSupportBookingDetails,
  cancelBookingUserSide,
  cancelBookingInstantRefund,
  cancelBookingHotelNormalRefund,
  sendCancelWarningToHotel,
  resendBookingVoucherAndNotifications,
} from '../api/adminPanelApi';
import { useAuth } from '../contexts/AuthContext';
import { can, canIvrC2c, FEATURES } from '../utils/permissions';
import IvrClickToCall from '../components/IvrClickToCall';
import { normalizeIvrPhone } from '../config/ivr';

/** @param {string} raw */
export function parseBookingSearchInput(raw) {
  const s = (raw || '').trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return { booking_id: Number(s) };
  return { booking_code: s };
}

function isAccessDeniedMessage(msg) {
  if (!msg || typeof msg !== 'string') return false;
  const m = msg.toLowerCase();
  return (
    m.includes('access denied') ||
    m.includes('no access') ||
    m.includes('not authorized') ||
    m.includes('unauthorised') ||
    m.includes('unauthorized') ||
    m.includes('permission denied') ||
    m.includes('forbidden') ||
    m.includes('cannot access') ||
    (m.includes('restrict') && m.includes('property'))
  );
}

function getCurrencyFromBooking(gb) {
  const c =
    gb?.currency ||
    gb?.amounts?.currency ||
    gb?.payment?.currency ||
    (gb?.payment && typeof gb.payment === 'object' ? gb.payment.currency : null);
  return (c || 'INR').toString().toUpperCase();
}

function formatMoney(value, currency = 'INR') {
  if (value === null || value === undefined || value === '') return '–';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency} ${num.toLocaleString('en-IN')}`;
  }
}

function normStr(v) {
  return (v == null ? '' : String(v)).trim().toLowerCase();
}

function parseRefundDetails(rd) {
  if (rd == null) return null;
  if (typeof rd === 'string') {
    try {
      return JSON.parse(rd);
    } catch {
      return null;
    }
  }
  if (typeof rd === 'object') return rd;
  return null;
}

function paymentStatusFromBooking(gb) {
  if (!gb) return '';
  return gb.payment_status ?? (gb.payment && typeof gb.payment === 'object' ? gb.payment.payment_status : '') ?? '';
}

/** Same gate as product UI: cancelled + paid + refunded + refund_details */
function shouldShowRefundTrack(gb) {
  if (!gb) return false;
  const rd = parseRefundDetails(gb.refund_details);
  if (!rd) return false;
  const bookingStatus = normStr(gb.booking_status || gb.status);
  const payStatus = normStr(paymentStatusFromBooking(gb));
  const refundStatus = normStr(gb.refund_status);
  return bookingStatus === 'cancelled' && payStatus === 'paid' && refundStatus === 'refunded';
}

function isBookingCancelled(gb) {
  if (!gb) return false;
  const s = normStr(gb.booking_status || gb.status);
  return s === 'cancelled' || s === 'canceled';
}

/** Prefer full voucher URL; fall back to API-relative path when needed. */
function resolveVoucherUrl(gb, kind) {
  if (!gb) return null;
  if (kind === 'guest') {
    const url = gb.guest_voucher_url || gb.booking_voucher_url;
    if (url && String(url).trim()) return String(url).trim();
    const path = gb.guest_voucher_path || gb.booking_voucher_path;
    if (path && String(path).trim()) {
      const p = String(path).trim();
      if (/^https?:\/\//i.test(p)) return p;
      return `https://api.tabtrips.com/olivrweb/hotelapi/${p.replace(/^\//, '')}`;
    }
    return null;
  }
  const url = gb.hotelier_voucher_url;
  if (url && String(url).trim()) return String(url).trim();
  const path = gb.hotelier_voucher_path;
  if (path && String(path).trim()) {
    const p = String(path).trim();
    if (/^https?:\/\//i.test(p)) return p;
    return `https://api.tabtrips.com/olivrweb/hotelapi/${p.replace(/^\//, '')}`;
  }
  return null;
}

function formatRefundDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function finalPaidAmountForRefund(gb) {
  if (!gb) return null;
  if (gb.final_amount_paid != null && gb.final_amount_paid !== '') return gb.final_amount_paid;
  const a = gb.amounts && typeof gb.amounts === 'object' ? gb.amounts : {};
  const keys = ['final_amount_paid_by_guest', 'final_paid_by_guest', 'amount_paid_by_guest'];
  for (const k of keys) {
    if (a[k] != null && a[k] !== '') return a[k];
  }
  return null;
}

function pickAmount(amounts, keys) {
  if (!amounts || typeof amounts !== 'object') return null;
  for (const k of keys) {
    if (amounts[k] !== undefined && amounts[k] !== null && amounts[k] !== '') {
      return amounts[k];
    }
  }
  return null;
}

function formatRoomSelection(value) {
  if (value == null) return '–';
  if (Array.isArray(value)) {
    return value.map((x) => String(x)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function Row({ label, children, mono, valueSx = {} }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography component="span" variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        component="span"
        variant="body2"
        sx={{
          flex: 1,
          fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
          wordBreak: 'break-word',
          ...valueSx,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function Subsection({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function RefundTrackDialog({ open, onClose, gb }) {
  if (!gb) return null;
  const rd = parseRefundDetails(gb.refund_details) || {};
  const currency = getCurrencyFromBooking(gb);
  const paid = finalPaidAmountForRefund(gb);
  const amtDisplay = formatMoney(paid ?? 0, currency);
  const isProcessed = normStr(rd.status) === 'processed';
  const dateStr = formatRefundDateTime(rd.refund_created_at);

  const steps = [
    { label: 'Initiation', date: dateStr },
    {
      label: 'Approval',
      date: dateStr,
      sub: isProcessed
        ? `${amtDisplay} has been refunded through original source of payment`
        : 'Refund will be processed to original source of payment',
    },
    { label: 'Refund process initiated', date: dateStr },
    {
      label: 'Refund status',
      date: '',
      sub: isProcessed
        ? [
            'Refund successfully processed',
            'Refund has been processed to the original source of payment.',
            ...(rd.rrn ? [`RRN: ${rd.rrn}`] : rd.arn ? [`ARN: ${rd.arn}`] : []),
          ]
        : ['Pending', 'Refund will be processed to the original source of payment.'],
      isPending: !isProcessed,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pr: 1, pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Refund status
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Booking ID: {gb.booking_code || gb.booking_id}
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
          Refund of <Box component="span" sx={{ color: 'success.main' }}>{amtDisplay}</Box> successfully transferred
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Refund reason: Refund within TabTrips Policy
        </Typography>

        <Box sx={{ position: 'relative', pl: 0.5 }}>
          {/* Vertical rail — sits in marker column only, never over text */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: 12,
              top: 10,
              bottom: 28,
              width: 2,
              bgcolor: 'success.main',
              borderRadius: 1,
            }}
          />
          {steps.map((step, i) => (
            <Box
              key={step.label}
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                pb: i === steps.length - 1 ? 0 : 2.5,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 26,
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  pt: 0.125,
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    border: 2,
                    borderStyle: 'solid',
                    borderColor: step.isPending ? 'warning.main' : 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  {step.isPending ? (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  )}
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {step.date ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {step.date}
                  </Typography>
                ) : null}
                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: step.date ? 0.25 : 0 }}>
                  {step.label}
                </Typography>
                {step.sub ? (
                  <Box sx={{ mt: 0.5 }}>
                    {Array.isArray(step.sub) ? (
                      step.sub.map((line, j) => (
                        <Typography key={j} variant="body2" color="text.secondary" display="block">
                          {line}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {step.sub}
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          Refund usually takes 4 to 7 business days to reflect.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function promoIsEmpty(promo) {
  if (promo == null) return true;
  if (typeof promo !== 'object') return false;
  return Object.values(promo).every((v) => v === null || v === undefined || v === '');
}

function pickGuestName(regUser, guestOn) {
  const candidates = [
    regUser?.name,
    guestOn?.guest_name,
    guestOn?.name,
    regUser?.full_name,
    guestOn?.full_name,
    regUser?.guest_name,
    guestOn?.customer_name,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim();
  }
  return null;
}

function GuestField({ label, value, mono }) {
  const display = value != null && String(value).trim() !== '' ? String(value).trim() : null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="div"
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          fontSize: '0.62rem',
          lineHeight: 1.2,
          letterSpacing: '0.06em',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          color: display ? 'text.primary' : 'text.disabled',
          fontStyle: display ? 'normal' : 'italic',
          fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
          fontSize: '0.875rem',
          wordBreak: 'break-word',
          lineHeight: 1.3,
        }}
      >
        {display ?? 'Not provided'}
      </Typography>
    </Box>
  );
}

function GuestHeaderCard({ regUser, guestOn, showIvrCall }) {
  const name = pickGuestName(regUser, guestOn);
  const phoneRaw =
    regUser?.phone_display ??
    guestOn?.guest_phone_display ??
    regUser?.phone ??
    guestOn?.guest_phone ??
    guestOn?.phone ??
    null;
  const phone = phoneRaw != null && String(phoneRaw).trim() !== '' ? String(phoneRaw).trim() : null;
  const userId = regUser?.user_id;
  const emailReg = regUser?.email && String(regUser.email).trim() !== '' ? String(regUser.email).trim() : null;
  const emailGuest = guestOn?.guest_email && String(guestOn.guest_email).trim() !== '' ? String(guestOn.guest_email).trim() : null;

  const emailBlocks = [];
  if (emailReg && emailGuest && emailReg.toLowerCase() === emailGuest.toLowerCase()) {
    emailBlocks.push({ label: 'Email', value: emailReg });
  } else {
    if (emailReg) emailBlocks.push({ label: 'Account email', value: emailReg });
    if (emailGuest) emailBlocks.push({ label: 'Email on booking', value: emailGuest });
  }

  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 1.5,
        borderRadius: 1.5,
        borderLeft: 3,
        borderColor: 'primary.main',
        bgcolor: 'rgba(255, 119, 0, 0.06)',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
        <PersonIcon color="primary" sx={{ fontSize: 22 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E4057', fontSize: '1rem', lineHeight: 1.2, flex: 1 }}>
          Guest
        </Typography>
        {showIvrCall && phone && normalizeIvrPhone(phone).length >= 10 && (
          <IvrClickToCall phone={phone} label="Call guest" size="small" />
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: userId != null ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
            },
          }}
        >
          {userId != null && <GuestField label="User ID" value={String(userId)} mono />}
          <GuestField label="Name" value={name} />
          <GuestField label="Phone" value={phone} mono />
        </Box>

        {emailBlocks.length > 0 && (
          <Box
            sx={{
              pt: 0.75,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'grid',
              gap: 1.25,
              gridTemplateColumns: { xs: '1fr', sm: emailBlocks.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr' },
            }}
          >
            {emailBlocks.map((b) => (
              <GuestField key={b.label} label={b.label} value={b.value} />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

const SupportBookingLookup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showIvrCall = canIvrC2c(user);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [guestBooking, setGuestBooking] = useState(null);
  const [propertyHost, setPropertyHost] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);

  const runSearch = useCallback(async () => {
    const parsed = parseBookingSearchInput(query);
    if (!parsed) {
      setError('Enter a booking ID (numbers only) or booking code (e.g. TT…).');
      setAccessDenied(false);
      setGuestBooking(null);
      setPropertyHost(null);
      setInfoMessage(null);
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    setGuestBooking(null);
    setPropertyHost(null);
    setInfoMessage(null);
    try {
      const res = await getSupportBookingDetails(parsed);
      if (!res.success) {
        const msg = res.error || res.data?.message || res.raw?.message || 'Could not load booking.';
        setError(msg);
        setAccessDenied(isAccessDeniedMessage(msg));
        return;
      }
      setGuestBooking(res.guest_booking);
      setPropertyHost(res.property_and_host);
      if (!res.guest_booking && !res.property_and_host) {
        setInfoMessage('Request succeeded but the response did not include guest_booking or property_and_host.');
      }
    } catch (e) {
      const msg = e.message || 'Request failed';
      setError(msg);
      setAccessDenied(isAccessDeniedMessage(msg));
    } finally {
      setLoading(false);
    }
  }, [query]);

  const gb = guestBooking;
  const guestVoucherUrl = useMemo(() => resolveVoucherUrl(gb, 'guest'), [gb]);
  const hotelierVoucherUrl = useMemo(() => resolveVoucherUrl(gb, 'hotelier'), [gb]);
  const currency = gb ? getCurrencyFromBooking(gb) : 'INR';
  const amounts = gb?.amounts && typeof gb.amounts === 'object' ? gb.amounts : {};

  const payment = useMemo(() => {
    if (!gb) return null;
    if (gb.payment && typeof gb.payment === 'object') return gb.payment;
    return {
      payment_method: gb.payment_method,
      payment_status: gb.payment_status,
      method: gb.payment_method,
      status: gb.payment_status,
      razorpay_payment_id: gb.razorpay_payment_id,
      razorpay_order_id: gb.razorpay_order_id,
      payment_id: gb.payment_id,
      order_id: gb.order_id,
    };
  }, [gb]);

  const paymentHasDetail = useMemo(() => {
    if (!payment || typeof payment !== 'object') return false;
    const keys = ['payment_method', 'payment_status', 'method', 'status', 'razorpay_payment_id', 'razorpay_order_id', 'payment_id', 'order_id'];
    return keys.some((k) => payment[k] !== null && payment[k] !== undefined && payment[k] !== '');
  }, [payment]);

  const hotel = propertyHost?.hotel ?? propertyHost?.property ?? null;
  const hostAccount = propertyHost?.host_account ?? propertyHost?.host ?? null;
  const guestOn = gb?.guest_on_booking ?? null;
  const regUser = gb?.registered_user_account;

  const guestCallPhone = useMemo(() => {
    const raw =
      regUser?.phone_display ??
      guestOn?.guest_phone_display ??
      regUser?.phone ??
      guestOn?.guest_phone ??
      guestOn?.phone ??
      null;
    return raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  }, [regUser, guestOn]);

  const hotelCallPhone = useMemo(() => {
    const raw = hotel?.phone ?? hotel?.hotel_phone ?? null;
    return raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  }, [hotel]);

  const hostCallPhone = useMemo(() => {
    const raw = hostAccount?.phone ?? null;
    return raw != null && String(raw).trim() !== '' ? String(raw).trim() : '';
  }, [hostAccount]);

  const canTransferBooking = can(user, FEATURES.transfer_booking);

  const bookingRef = useMemo(() => {
    if (!gb) return '';
    const code = (gb.booking_code ?? gb.code ?? '').toString().trim();
    if (code) return code;
    if (gb.booking_id != null && String(gb.booking_id).trim() !== '') return String(gb.booking_id);
    return '';
  }, [gb]);

  const supportNumericBookingId = useMemo(() => {
    if (!gb || gb.booking_id == null || String(gb.booking_id).trim() === '') return null;
    const n = Number(gb.booking_id);
    return Number.isNaN(n) ? null : n;
  }, [gb]);

  const supportHotelId = useMemo(() => {
    if (hotel) {
      const id = hotel.hotel_id ?? hotel.id;
      if (id != null && String(id).trim() !== '') return id;
    }
    if (gb?.hotel_id != null && String(gb.hotel_id).trim() !== '') return gb.hotel_id;
    return null;
  }, [hotel, gb]);

  const [confirmAction, setConfirmAction] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showRefundTrackPopup, setShowRefundTrackPopup] = useState(false);
  const [resendVoucherOpen, setResendVoucherOpen] = useState(false);
  const [resendScope, setResendScope] = useState('guest');
  const [resendSubmitting, setResendSubmitting] = useState(false);

  const handleResendVoucherSubmit = useCallback(async () => {
    if (supportNumericBookingId == null) return;
    setResendSubmitting(true);
    try {
      const payload = { booking_id: supportNumericBookingId, scope: resendScope };
      if (supportHotelId != null) payload.hotel_id = supportHotelId;
      const res = await resendBookingVoucherAndNotifications(payload);
      const bodyFailed = res.success && res.data && res.data.status === false;
      if (!res.success || bodyFailed) {
        const msg = !res.success ? res.error : res.data?.message || 'Resend failed';
        setSnackbar({ open: true, message: msg, severity: 'error' });
        return;
      }
      setSnackbar({
        open: true,
        message: res.data?.message || 'Voucher / notifications resent.',
        severity: 'success',
      });
      setResendVoucherOpen(false);
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Resend failed', severity: 'error' });
    } finally {
      setResendSubmitting(false);
    }
  }, [supportNumericBookingId, supportHotelId, resendScope]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction || !bookingRef) return;
    setActionSubmitting(true);
    try {
      let res;
      if (confirmAction === 'warning') {
        res = await sendCancelWarningToHotel(bookingRef);
      } else if (confirmAction === 'cancelGuestNormal') {
        res = await cancelBookingUserSide(bookingRef, supportHotelId);
      } else if (confirmAction === 'cancelHotelInstant') {
        res = await cancelBookingInstantRefund(bookingRef, supportHotelId);
      } else if (confirmAction === 'cancelHotelNormal') {
        res = await cancelBookingHotelNormalRefund(bookingRef, supportHotelId);
      } else {
        setSnackbar({ open: true, message: 'Unknown action', severity: 'error' });
        return;
      }

      const bodyFailed = res.success && res.data && res.data.status === false;
      if (!res.success || bodyFailed) {
        const msg = !res.success ? res.error : res.data?.message || 'Action failed';
        setSnackbar({ open: true, message: msg, severity: 'error' });
        return;
      }

      const okMsg =
        confirmAction === 'warning'
          ? 'Cancel warning sent to hotel.'
          : confirmAction === 'cancelGuestNormal'
            ? res.message || 'Cancelled (guest side) — normal refund started.'
            : confirmAction === 'cancelHotelInstant'
              ? res.message || 'Cancelled (hotel side) — instant refund started.'
              : res.message || 'Cancelled (hotel side) — normal refund started.';
      setSnackbar({ open: true, message: okMsg, severity: 'success' });
      setConfirmAction(null);
      await runSearch();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Request failed', severity: 'error' });
    } finally {
      setActionSubmitting(false);
    }
  }, [confirmAction, bookingRef, supportHotelId, runSearch]);

  const amountRows = useMemo(() => {
    const rows = [];
    const hlGuest = { fontWeight: 800, fontSize: '1.1rem', color: '#1b5e20' };
    const hlHotel = { fontWeight: 800, fontSize: '1.1rem', color: '#0d47a1' };

    const tTariff = pickAmount(amounts, ['total_amount_room_tariff']);
    const tSub = pickAmount(amounts, ['discounted_amount_subtotal']);
    const tGst = pickAmount(amounts, ['gst_amount', 'gst_amount_total']);
    const tPaidGuest = pickAmount(amounts, [
      'final_amount_paid_by_guest',
      'final_paid_by_guest',
      'amount_paid_by_guest',
      'final_amount_guest',
    ]);
    const tPayHotel = pickAmount(amounts, [
      'final_pay_to_hotel_tabtrips_side',
      'final_pay_to_hotel',
      'pay_to_hotel',
      'amount_payable_to_hotel',
    ]);

    rows.push({
      key: 'tariff',
      label: 'Total amount room tariff',
      value: formatMoney(tTariff, currency),
    });
    rows.push({
      key: 'sub',
      label: 'Discounted amount subtotal',
      value: formatMoney(tSub, currency),
    });
    rows.push({
      key: 'gst',
      label: 'GST amount',
      value: formatMoney(tGst, currency),
    });
    rows.push({
      key: 'paid_guest',
      label: 'Final amount paid by guest',
      value: formatMoney(tPaidGuest, currency),
      valueSx: hlGuest,
    });
    rows.push({
      key: 'pay_hotel',
      label: 'Final pay to hotel (Tabtrips side)',
      value: formatMoney(tPayHotel, currency),
      valueSx: hlHotel,
    });

    const commRaw = pickAmount(amounts, ['tabtrips_commission', 'tabtrips_commission_amount']);
    const commGstRaw = pickAmount(amounts, ['gst_on_tabtrips_commission', 'gst_on_commission', 'gst_on_tabtrips_commision']);
    if (commRaw != null || commGstRaw != null) {
      const comm = Number(commRaw);
      const commGst = Number(commGstRaw);
      const sum = (Number.isNaN(comm) ? 0 : comm) + (Number.isNaN(commGst) ? 0 : commGst);
      rows.push({
        key: 'comm_combined',
        label: 'Tabtrips commission + commission GST',
        value: formatMoney(sum, currency),
      });
    }

    return rows;
  }, [amounts, currency]);

  const promoBlock = useMemo(() => {
    const p = gb?.promo;
    if (promoIsEmpty(p)) {
      return <Typography variant="body2" color="text.secondary">null</Typography>;
    }
    if (p && typeof p === 'object' && p.promo_code != null && String(p.promo_code).trim() !== '') {
      return (
        <Typography variant="body2" fontWeight={600}>
          {String(p.promo_code)}
        </Typography>
      );
    }
    return <Typography variant="body2">null</Typography>;
  }, [gb]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/')} sx={{ color: '#FF7700' }}>
          <ArrowBackIcon />
        </IconButton>
        <ReceiptLongIcon sx={{ color: '#2E4057', fontSize: 36 }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
            Booking details (support)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lookup by numeric ID or code (e.g. TT…). Digits only → booking_id; otherwise → booking_code.
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, flex: '1 1 280px' }}>
            <TextField
              size="small"
              label="Booking ID or code"
              placeholder="e.g. 12345 or TT63459540"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              sx={{ minWidth: 220, flex: '1 1 260px', maxWidth: 420 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              disabled={loading || !query.trim()}
              onClick={runSearch}
              sx={{
                py: 1,
                px: 2.5,
                height: 40,
                flexShrink: 0,
                backgroundColor: '#FF7700',
                '&:hover': { backgroundColor: '#E65100' },
              }}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            >
              {loading ? 'Loading…' : 'Search'}
            </Button>
          </Box>

          {showIvrCall && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-end',
                ml: 'auto',
              }}
            >
              <IvrClickToCall
                phone={guestCallPhone}
                label="Call guest"
                size="medium"
                variant="contained"
                context="booking_guest"
                bookingId={supportNumericBookingId ?? bookingRef}
                disabled={!guestCallPhone || normalizeIvrPhone(guestCallPhone).length < 10}
                sx={{
                  height: 40,
                  bgcolor: '#2e7d32',
                  '&:hover': { bgcolor: '#1b5e20' },
                  '&.Mui-disabled': { bgcolor: '#a5d6a7', color: '#fff' },
                }}
              />
              <IvrClickToCall
                phone={hotelCallPhone}
                label="Call hotel"
                size="medium"
                variant="contained"
                context="booking_hotel"
                bookingId={supportNumericBookingId ?? bookingRef}
                disabled={!hotelCallPhone || normalizeIvrPhone(hotelCallPhone).length < 10}
                sx={{
                  height: 40,
                  bgcolor: '#1565c0',
                  '&:hover': { bgcolor: '#0d47a1' },
                  '&.Mui-disabled': { bgcolor: '#90caf9', color: '#fff' },
                }}
              />
              <IvrClickToCall
                phone={hostCallPhone}
                label="Call host"
                size="medium"
                variant="contained"
                context="booking_host"
                bookingId={supportNumericBookingId ?? bookingRef}
                disabled={!hostCallPhone || normalizeIvrPhone(hostCallPhone).length < 10}
                sx={{
                  height: 40,
                  bgcolor: '#6a1b9a',
                  '&:hover': { bgcolor: '#4a148c' },
                  '&.Mui-disabled': { bgcolor: '#ce93d8', color: '#fff' },
                }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      {infoMessage && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfoMessage(null)}>
          {infoMessage}
        </Alert>
      )}

      {error && (
        <Alert
          severity={accessDenied ? 'warning' : 'error'}
          sx={{ mb: 2 }}
          onClose={accessDenied ? undefined : () => setError(null)}
        >
          {accessDenied ? (
            <Box>
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                You do not have access to this property&apos;s booking details.
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Box>
          ) : (
            error
          )}
        </Alert>
      )}

      {!gb && !hotel && !loading && !error && query.trim() === '' && (
        <Typography variant="body2" color="text.secondary">
          Enter a booking reference and press Search.
        </Typography>
      )}

      {(gb || hotel || hostAccount) && (
        <>
          {gb && <GuestHeaderCard regUser={regUser} guestOn={guestOn} showIvrCall={false} />}

          {gb &&
            (bookingRef || shouldShowRefundTrack(gb) || supportNumericBookingId != null) && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.25,
                mb: 2,
              }}
            >
              {bookingRef && (
                <>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="medium"
                    startIcon={<WarningAmberIcon fontSize="small" />}
                    disabled={loading || actionSubmitting}
                    onClick={() => setConfirmAction('warning')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      borderWidth: 1.5,
                      px: 2,
                    }}
                  >
                    Send cancel warning
                  </Button>
                  {!isBookingCancelled(gb) && (
                    <>
                      <Button
                        variant="outlined"
                        color="error"
                        size="medium"
                        startIcon={<PersonIcon fontSize="small" />}
                        disabled={loading || actionSubmitting}
                        onClick={() => setConfirmAction('cancelGuestNormal')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 1.5,
                          borderWidth: 1.5,
                          px: 2,
                        }}
                      >
                        Guest cancel · normal refund
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="medium"
                        startIcon={<HotelIcon fontSize="small" />}
                        disabled={loading || actionSubmitting}
                        onClick={() => setConfirmAction('cancelHotelNormal')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 1.5,
                          borderWidth: 1.5,
                          px: 2,
                        }}
                      >
                        Hotel cancel · normal refund
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="medium"
                        startIcon={<FlashOnIcon fontSize="small" />}
                        disabled={loading || actionSubmitting}
                        onClick={() => setConfirmAction('cancelHotelInstant')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 1.5,
                          px: 2,
                          boxShadow: 'none',
                          '&:hover': { boxShadow: '0 2px 8px rgba(211, 47, 47, 0.35)' },
                        }}
                      >
                        Hotel cancel · instant refund
                      </Button>
                    </>
                  )}
                </>
              )}
              {shouldShowRefundTrack(gb) && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="medium"
                  startIcon={<RefreshIcon fontSize="small" />}
                  disabled={loading || actionSubmitting}
                  onClick={() => setShowRefundTrackPopup(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    borderWidth: 1.5,
                    px: 2,
                  }}
                >
                  Track refund
                </Button>
              )}
              {supportNumericBookingId != null && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<ForwardToInboxIcon fontSize="small" />}
                  disabled={loading || actionSubmitting || resendSubmitting}
                  onClick={() => {
                    setResendScope('guest');
                    setResendVoucherOpen(true);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    borderWidth: 1.5,
                    px: 2,
                  }}
                >
                  Resend voucher &amp; notifications
                </Button>
              )}
            </Box>
          )}

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptLongIcon color="primary" />
                  Guest / booking
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {!gb && <Typography color="text.secondary">No guest booking block in response.</Typography>}
                {gb && (
                  <>
                    <Row label="Booking code">{gb.booking_code ?? gb.code ?? '–'}</Row>
                    <Row label="Booking ID" mono>
                      {gb.booking_id != null ? String(gb.booking_id) : '–'}
                    </Row>
                    <Row label="Status">
                      {gb.booking_status != null && gb.booking_status !== '' ? (
                        <Chip size="small" label={String(gb.booking_status)} />
                      ) : gb.status != null ? (
                        <Chip size="small" label={String(gb.status)} />
                      ) : (
                        '–'
                      )}
                    </Row>
                    <Row label="Booking created" mono>
                      {formatRefundDateTime(gb.created_at ?? gb.timestamps?.created_at)}
                    </Row>
                    <Row label="Check-in">{gb.check_in_date ?? gb.check_in ?? gb.checkin ?? gb.checkin_date ?? '–'}</Row>
                    <Row label="Check-out">{gb.check_out_date ?? gb.check_out ?? gb.checkout ?? gb.checkout_date ?? '–'}</Row>
                    <Row label="Nights">{gb.total_nights ?? gb.nights ?? gb.no_of_nights ?? '–'}</Row>
                    <Row label="Guests">
                      {gb.total_guests != null
                        ? String(gb.total_guests)
                        : gb.guests != null
                        ? String(gb.guests)
                        : [gb.adults, gb.children].some((x) => x != null)
                        ? `Adults: ${gb.adults ?? '–'}, Children: ${gb.children ?? '–'}`
                        : '–'}
                    </Row>
                    <Row label="Room selection">{formatRoomSelection(gb.room_selection_summary ?? gb.room_summary)}</Row>

                    <Subsection title="Amounts">
                      {amountRows.map((r) => (
                        <Row key={r.key} label={r.label} valueSx={r.valueSx || {}}>
                          {r.value}
                        </Row>
                      ))}
                    </Subsection>

                    <Subsection title="Promo">{promoBlock}</Subsection>

                    <Subsection title="Payment">
                      {!paymentHasDetail ? (
                        <Typography variant="body2" color="text.secondary">
                          No payment details.
                        </Typography>
                      ) : (
                        <>
                          <Row label="Method">{payment.payment_method ?? payment.method ?? '–'}</Row>
                          <Row label="Status">{payment.payment_status ?? payment.status ?? '–'}</Row>
                          <Row label="Payment ID" mono>
                            {payment.razorpay_payment_id ?? payment.payment_id ?? '–'}
                          </Row>
                          <Row label="Order ID" mono>
                            {payment.razorpay_order_id ?? payment.order_id ?? '–'}
                          </Row>
                        </>
                      )}
                    </Subsection>

                    {shouldShowRefundTrack(gb) && (() => {
                      const rd = parseRefundDetails(gb.refund_details) || {};
                      return (
                        <Subsection title="Refund">
                          <Row label="Booking refund status">{gb.refund_status ?? '–'}</Row>
                          <Row label="Gateway / refund record">{rd.status ?? '–'}</Row>
                          <Row label="Refund initiated" mono>
                            {formatRefundDateTime(rd.refund_created_at)}
                          </Row>
                          {(rd.rrn || rd.arn) && (
                            <Row label="RRN / ARN" mono>
                              {[rd.rrn && `RRN: ${rd.rrn}`, rd.arn && `ARN: ${rd.arn}`].filter(Boolean).join(' · ')}
                            </Row>
                          )}
                        </Subsection>
                      );
                    })()}
                  </>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" />
                  Property / host
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {!hotel && !hostAccount && (
                  <Typography color="text.secondary">No property or host block in response.</Typography>
                )}

                {hotel && (
                  <Subsection title="Hotel / property">
                    <Row label="Hotel ID">{hotel.hotel_id != null ? String(hotel.hotel_id) : '–'}</Row>
                    <Row label="Name">{hotel.hotel_name ?? hotel.name ?? '–'}</Row>
                    <Row label="Address">
                      {(() => {
                        const addr =
                          hotel.address ??
                          ([hotel.address_line1, hotel.city, hotel.state, hotel.pincode].filter(Boolean).join(', ') ||
                            null);
                        if (addr == null || addr === '') return '–';
                        if (typeof addr === 'object') {
                          return JSON.stringify(addr, null, 2);
                        }
                        return String(addr);
                      })()}
                    </Row>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        py: 0.75,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 160, fontWeight: 600 }}
                      >
                        Phone
                      </Typography>
                      <Typography component="span" variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                        {hotel.phone ?? hotel.hotel_phone ?? '–'}
                      </Typography>
                      {showIvrCall &&
                        normalizeIvrPhone(hotel.phone ?? hotel.hotel_phone).length >= 10 && (
                          <IvrClickToCall
                            phone={hotel.phone ?? hotel.hotel_phone}
                            label="Call hotel"
                            size="small"
                            context="booking_hotel"
                            bookingId={supportNumericBookingId ?? bookingRef}
                          />
                        )}
                    </Box>
                    <Row label="Listing email">{hotel.hotel_listing_email ?? hotel.listing_email ?? '–'}</Row>
                  </Subsection>
                )}

                {hostAccount && (
                  <Subsection title="Host account">
                    <Row label="Name">{hostAccount.name ?? hostAccount.host_name ?? '–'}</Row>
                    <Row label="Email">{hostAccount.email ?? '–'}</Row>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        py: 0.75,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 160, fontWeight: 600 }}
                      >
                        Phone
                      </Typography>
                      <Typography component="span" variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                        {hostAccount.phone ?? '–'}
                      </Typography>
                      {showIvrCall && normalizeIvrPhone(hostAccount.phone).length >= 10 && (
                        <IvrClickToCall
                          phone={hostAccount.phone}
                          label="Call host"
                          size="small"
                          context="booking_host"
                          bookingId={supportNumericBookingId ?? bookingRef}
                        />
                      )}
                    </Box>
                  </Subsection>
                )}
              </Paper>
            </Grid>
          </Grid>

          {gb && (guestVoucherUrl || hotelierVoucherUrl) && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: '#2E4057' }}>
                Vouchers
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<DescriptionIcon fontSize="small" />}
                  disabled={!guestVoucherUrl}
                  onClick={() => {
                    if (guestVoucherUrl) window.open(guestVoucherUrl, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: 2,
                    bgcolor: '#FF7700',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#e65100', boxShadow: 'none' },
                  }}
                >
                  Guest voucher
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<HotelIcon fontSize="small" />}
                  disabled={!hotelierVoucherUrl}
                  onClick={() => {
                    if (hotelierVoucherUrl) window.open(hotelierVoucherUrl, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    px: 2,
                    bgcolor: '#2E4057',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#1A252F', boxShadow: 'none' },
                  }}
                >
                  Hotelier voucher
                </Button>
              </Box>
            </Paper>
          )}

          {canTransferBooking && gb && (supportNumericBookingId != null || bookingRef) && (
            <Paper
              sx={{
                p: 2,
                mt: 2,
                borderRadius: 2,
                border: '1px solid #bbdefb',
                bgcolor: '#f5f9ff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E4057' }}>
                    Transfer booking
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Move this guest to a nearby hotel (same flow as Transfer Booking page).
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => {
                    const id =
                      supportNumericBookingId != null
                        ? String(supportNumericBookingId)
                        : String(bookingRef);
                    navigate('/transfer-booking', {
                      state: { bookingId: id, autoLookup: true },
                    });
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: '#1565c0',
                    px: 3,
                    '&:hover': { bgcolor: '#0d47a1' },
                  }}
                >
                  Transfer booking
                </Button>
              </Box>
            </Paper>
          )}
        </>
      )}

      <RefundTrackDialog
        open={Boolean(showRefundTrackPopup && gb && shouldShowRefundTrack(gb))}
        onClose={() => setShowRefundTrackPopup(false)}
        gb={gb}
      />

      <Dialog
        open={confirmAction != null}
        onClose={() => !actionSubmitting && setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmAction === 'warning' && 'Send cancel warning?'}
          {confirmAction === 'cancelGuestNormal' && 'Guest cancel · normal refund'}
          {confirmAction === 'cancelHotelNormal' && 'Hotel cancel · normal refund'}
          {confirmAction === 'cancelHotelInstant' && 'Hotel cancel · instant refund'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirmAction === 'warning' && (
              <>
                Send a <strong>cancel warning</strong> to the hotel for <strong>{bookingRef}</strong>?
              </>
            )}
            {confirmAction === 'cancelGuestNormal' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Treated as <strong>guest / user cancel</strong>. Guest gets a <strong>normal refund</strong>{' '}
                  (usually 5–7 days). WhatsApp: guest self-cancel + hotel normal cancel.
                </Typography>
                Booking: <strong>{bookingRef}</strong>
                {supportHotelId != null && (
                  <>
                    {' '}
                    · Hotel: <strong>{String(supportHotelId)}</strong>
                  </>
                )}
              </>
            )}
            {confirmAction === 'cancelHotelNormal' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Treated as <strong>hotel cancel</strong>. Guest gets a <strong>normal refund</strong>{' '}
                  (usually 5–7 days). WhatsApp: hotel-side cancel messages to guest + hotel.
                </Typography>
                Booking: <strong>{bookingRef}</strong>
                {supportHotelId != null && (
                  <>
                    {' '}
                    · Hotel: <strong>{String(supportHotelId)}</strong>
                  </>
                )}
              </>
            )}
            {confirmAction === 'cancelHotelInstant' && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Treated as <strong>hotel cancel</strong>. Guest gets an <strong>instant full refund</strong>.
                  WhatsApp: hotel-side cancel messages to guest + hotel. Cannot undo from here.
                </Typography>
                Booking: <strong>{bookingRef}</strong>
                {supportHotelId != null && (
                  <>
                    {' '}
                    · Hotel: <strong>{String(supportHotelId)}</strong>
                  </>
                )}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAction(null)} disabled={actionSubmitting}>
            No
          </Button>
          <Button
            variant="contained"
            color={
              confirmAction === 'cancelGuestNormal' ||
              confirmAction === 'cancelHotelNormal' ||
              confirmAction === 'cancelHotelInstant'
                ? 'error'
                : confirmAction === 'warning'
                  ? 'warning'
                  : 'primary'
            }
            onClick={handleConfirmAction}
            disabled={actionSubmitting}
          >
            {actionSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Confirm cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resendVoucherOpen}
        onClose={() => !resendSubmitting && setResendVoucherOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Resend voucher &amp; notifications</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 1 }}>
            Sends the API request with <strong>booking_id</strong> {supportNumericBookingId ?? '–'} and your chosen{' '}
            <strong>scope</strong>.
            {supportHotelId != null ? (
              <>
                {' '}
                <strong>hotel_id</strong> from this page: {String(supportHotelId)}.
              </>
            ) : (
              <>
                {' '}
                No hotel block in the response — request will use <strong>booking_id</strong> and <strong>scope</strong>{' '}
                only.
              </>
            )}
          </DialogContentText>
          {supportHotelId == null && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Property / hotel ID is missing. If the API requires <code>hotel_id</code>, load a booking your admin can
              access fully or confirm with backend.
            </Alert>
          )}
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">Scope</FormLabel>
            <RadioGroup
              name="resend-voucher-scope"
              value={resendScope}
              onChange={(e) => setResendScope(e.target.value)}
            >
              <FormControlLabel value="guest" control={<Radio />} label="Guest" />
              <FormControlLabel value="hotelier" control={<Radio />} label="Hotelier" />
              <FormControlLabel value="both" control={<Radio />} label="Both" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResendVoucherOpen(false)} disabled={resendSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResendVoucherSubmit}
            disabled={resendSubmitting}
            sx={{ backgroundColor: '#FF7700', '&:hover': { backgroundColor: '#E65100' } }}
          >
            {resendSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportBookingLookup;
